/**
 * Which of Lavendla's services a chosen city can be entered through, and which
 * of them the visitor most likely wants. Shared by the city panel, which lists
 * them, and the mobile menu footer, which links straight to the likeliest one —
 * so the rule for "the one service" has a single definition.
 */

interface TCityLink {
  title: string;
  url: string;
}

export type TCityLinks = Partial<
  Record<"funeral" | "lawyer" | "therapy", TCityLink | null>
>;

export type TCityServiceTexts = Record<string, string>;

export interface TCityService {
  brand: string;
  city: string;
  key: string;
  label: string;
  title: string;
  url: string;
}

/**
 * `postType` is what the site has to register for the service to exist here at
 * all; `link` is the key the city-links endpoint answers under.
 */
const SERVICES = [
  {
    brand: "brand",
    key: "funeral",
    label: "funeral",
    link: "funeral",
    postType: "city",
  },
  {
    brand: "brandLawyer",
    key: "lawyer",
    label: "law",
    link: "lawyer",
    postType: "jurist",
  },
  {
    brand: "brandTherapy",
    key: "therapy",
    label: "therapy",
    link: "therapy",
    postType: "therapy",
  },
] as const;

/** Named in the order above, whichever of them this city and site allow. */
export function buildCityServices(
  cityLinks: TCityLinks | null,
  texts: TCityServiceTexts,
  registeredPostTypes: readonly string[]
): TCityService[] {
  if (!cityLinks) {
    return [];
  }

  return SERVICES.filter(
    ({ postType, link }) =>
      registeredPostTypes.includes(postType) && cityLinks[link]
  ).map(({ key, link, label, brand }) => {
    const cityLink = cityLinks[link] as TCityLink;

    return {
      brand: texts[brand],
      city: cityLink.title,
      key,
      label: texts[label],
      title: `${texts[brand]} ${cityLink.title}`,
      url: cityLink.url,
    };
  });
}

/**
 * The service the visitor was last on, remembered from an earlier page type.
 * Null until there is one to remember, and null again in a city that has no
 * page for it — in both cases nothing here is likelier than anything else.
 */
export function pickPreferredService(
  services: readonly TCityService[],
  roleTerm: string | null
): TCityService | null {
  if (!roleTerm) {
    return null;
  }

  return services.find((service) => service.key === roleTerm) ?? null;
}
